<script setup lang="ts">
import {
	onMounted,
	reactive,
	ref,
	computed,
	watch,
	onBeforeUnmount,
} from 'vue';
import ModalLayout from '@zyro-inc/site-modules/components/ecommerce/modals/partials/ModalLayout.vue';
import GridInput from '@zyro-inc/site-modules/components/elements/input/GridInput.vue';
import GridButton from '@zyro-inc/site-modules/components/elements/button/GridButton.vue';
import validateNumber, { NumberValidationOptions } from '@zyro-inc/site-modules/utils/validateNumber';
import validateString, { StringValidationOptions } from '@zyro-inc/site-modules/utils/validateString';
import RatingPicker from '@zyro-inc/site-modules/components/ecommerce/-partials/RatingPicker.vue';
import { useEcommerceModal } from '@zyro-inc/site-modules/components/ecommerce/modals/useEcommerceModal';
import ProductMediaElement from '@zyro-inc/site-modules/components/ecommerce/ProductMediaElement.vue';
import { useProductReview } from '@zyro-inc/site-modules/use/useProductReview';
import IconSuccess from '@zyro-inc/site-modules/components/icons/IconSuccess.vue';

interface FormValue<T> {
	value: T;
	errors: string[];
}

interface FormValues {
	name: FormValue<string>;
	email: FormValue<string>;
	review: FormValue<string>;
	rating: FormValue<number | undefined>;
}

type FormField = keyof FormValues;

const props = defineProps<{ translations: Record<string, string | undefined> }>();

const numberValidations: NumberValidationOptions[] = [
	{
		fieldName: 'rating',
		validations: [
			'required',
			'min',
			'max',
		],
		validationMessages: {
			required: props.translations.productReviewModalPleaseAddRating || 'Please add your rating',
			min: props.translations.productReviewModalRatingTooLow || 'Rating cannot be lower than 1',
			max: props.translations.productReviewModalRatingTooHigh || 'Rating cannot be higher than 5',
		},
		min: 1,
		max: 5,
	},
];

const stringValidations: StringValidationOptions[] = [
	{
		fieldName: 'email',
		validations: [
			'required',
			'email',
			'maxLength',
		],
		validationMessages: {
			required: props.translations.productReviewModalAddEmail || 'Please add your email',
			email: props.translations.productReviewModalEnterValidEmail || 'Please enter a valid email address',
			maxLength: props.translations.productReviewModalEmailTooLong || 'Email cannot be longer than 254 characters',
		},
		maxLength: 254,
	},
	{
		fieldName: 'name',
		validations: ['maxLength'],
		validationMessages: {
			maxLength: props.translations.productReviewModalNameTooLong || 'Name cannot be longer than 50 characters',
		},
		maxLength: 50,
	},
	{
		fieldName: 'review',
		validations: ['maxLength'],
		validationMessages: {
			maxLength: props.translations.productReviewModalReviewTooLong || 'Review cannot be longer than 700 characters',
		},
		maxLength: 700,
	},
];

const {
	createProductReviewData,
	closeEcommerceModal,
} = useEcommerceModal();

const {
	createProductReview,
	isCreating,
	isCreated,
	isError,
} = useProductReview();

const formValues = reactive<FormValues>({
	name: {
		value: '',
		errors: [],
	},
	email: {
		value: '',
		errors: [],
	},
	review: {
		value: '',
		errors: [],
	},
	rating: {
		value: undefined,
		errors: [],
	},
});

const showValidationErrors = ref(false);

const isFormValid = computed(() => Object.values(formValues).every((field) => field.errors.length === 0));
const isSubmitDisabled = computed(() => !createProductReviewData.value
	|| isCreating.value
	|| (showValidationErrors.value && !isFormValid.value));
const maxReviewLength = computed(() => stringValidations.find((validation) => validation.fieldName === 'review')?.maxLength);

const validateFormField = (field: FormField, value: string | number | undefined) => {
	const matchedNumberValidation = numberValidations.find((validation) => validation.fieldName === field);

	if (matchedNumberValidation) {
		const errors = validateNumber({
			value: value as number | undefined,
			options: matchedNumberValidation,
		});

		formValues[field].errors = errors.map(({ message }) => message);

		// Value is number, no need to check string validations
		return;
	}

	const matchedStringValidation = stringValidations.find((validation) => validation.fieldName === field);

	if (matchedStringValidation) {
		const errors = validateString({
			value: value as string,
			options: matchedStringValidation,
		});

		formValues[field].errors = errors.map(({ message }) => message);
	}
};

const handleSubmit = () => {
	Object.keys(formValues).forEach((field) => {
		validateFormField(field as FormField, formValues[field as FormField].value);
	});

	showValidationErrors.value = true;

	if (isFormValid.value && createProductReviewData.value) {
		createProductReview({
			storeId: createProductReviewData.value.storeId,
			productId: createProductReviewData.value.productId,
			rating: formValues.rating.value as number,
			email: formValues.email.value,
			reviewerName: formValues.name.value,
			comment: formValues.review.value,
		});
	}
};

const handleInputChange = (field: FormField, value: string | number | undefined) => {
	// Hide validation errors when input changes
	// This also allows the user to try submitting the form again
	formValues[field].errors = [];
	formValues[field].value = value;
};

const handleReviewInputChange = (field: FormField, value = '') => {
	const truncatedReviewText = value.substring(0, maxReviewLength.value);

	handleInputChange(field, truncatedReviewText);
};

let autoCloseTimer: ReturnType<typeof setTimeout> | undefined;

// Close the success modal automatically after 5 seconds
// Also clear the timer if the modal is closed
watch(isCreated, (newValue) => {
	if (newValue) {
		autoCloseTimer = setTimeout(() => {
			closeEcommerceModal();
		}, 5000);
	} else if (autoCloseTimer) {
		clearTimeout(autoCloseTimer);
		autoCloseTimer = undefined;
	}
});

// Try reading the review data from the URL query when the modal is opened
onMounted(() => {
	const params = new URLSearchParams(window.location.search);

	formValues.name.value = params.get('reviewerName') || '';
	formValues.email.value = params.get('reviewerEmail') || '';
	formValues.rating.value = Number(params.get('rating')) || undefined;
});

// Make sure the timer is cleared if the component is unmounted
onBeforeUnmount(() => {
	if (autoCloseTimer) {
		clearTimeout(autoCloseTimer);
	}
});
</script>

<template>
	<ModalLayout
		v-if="createProductReviewData"
		max-width="540px"
		width="100%"
	>
		<div
			v-if="isCreated"
			class="ecommerce-submit-review-modal-success"
		>
			<IconSuccess class="ecommerce-submit-review-modal-success__icon" />
			<h2 class="ecommerce-submit-review-modal-success__title">
				{{ translations.productReviewModalThankYou || 'Thank you!' }}
			</h2>
			<p class="ecommerce-submit-review-modal-success__description">
				{{ translations.productReviewModalReceived || 'We received your review. It will be published as soon as we verify it.' }}
			</p>
		</div>
		<div
			v-else
			class="ecommerce-submit-review-modal"
		>
			<h2 class="ecommerce-submit-review-modal__title">
				{{ translations.productReviewModalTitle || 'Review this product' }}
			</h2>
			<div class="ecommerce-submit-review-modal__product">
				<ProductMediaElement
					class="ecommerce-submit-review-modal__product-image"
					:src="createProductReviewData.productImage"
					:alt="createProductReviewData.productTitle"
					:width="64"
					:height="64"
					:site-id="createProductReviewData.storeId"
				/>
				<h3 class="ecommerce-submit-review-modal__product-title">
					{{ createProductReviewData.productTitle }}
				</h3>
			</div>
			<form
				class="ecommerce-submit-review-modal__form"
				@submit.prevent="handleSubmit"
			>
				<RatingPicker
					v-model="formValues.rating.value"
					class="ecommerce-submit-review-modal__rating"
					:max-value="5"
					:validation-error-messages="formValues.rating.errors"
					:are-errors-shown="showValidationErrors"
					@update:model-value="(value: number) => handleInputChange('rating', value)"
				/>
				<GridInput
					id="ecommerce-submit-review-modal-input-name"
					class="ecommerce-submit-review-modal__input"
					:model-value="formValues.name.value"
					:label="translations.name || 'Name'"
					theme="light"
					:validation-errors="formValues.name.errors"
					:are-errors-shown="showValidationErrors"
					@update:model-value="(value: string) => handleInputChange('name', value)"
				/>
				<GridInput
					id="ecommerce-submit-review-modal-input-email"
					class="ecommerce-submit-review-modal__input"
					:model-value="formValues.email.value"
					:label="translations.email || 'Email'"
					theme="light"
					is-required
					:is-required-asterisk-visible="false"
					:validation-errors="formValues.email.errors"
					:are-errors-shown="showValidationErrors"
					@update:model-value="(value: string) => handleInputChange('email', value)"
				/>
				<GridInput
					id="ecommerce-submit-review-modal-input-review"
					class="ecommerce-submit-review-modal__input"
					:model-value="formValues.review.value"
					:label="translations.review || 'Review'"
					theme="light"
					tag="textarea"
					:validation-errors="formValues.review.errors"
					:are-errors-shown="showValidationErrors"
					@update:model-value="(value: string) => handleReviewInputChange('review', value)"
				/>
				<div
					v-if="maxReviewLength"
					class="ecommerce-submit-review-modal__char-count"
				>
					{{ formValues.review.value.length }}/{{ maxReviewLength }}
				</div>
				<GridButton
					class="ecommerce-submit-review-modal__button"
					tag-name="button"
					type="primary"
					button-type="submit"
					:content="translations.submit || 'Submit'"
					:is-disabled="isSubmitDisabled"
					:is-in-builder="false"
				/>
				<span
					v-if="isError"
					class="ecommerce-submit-review-modal__error"
				>
					{{ translations.productReviewModalError || 'Could not submit the review. Please try again later.' }}
				</span>
			</form>
		</div>
	</ModalLayout>
</template>

<style lang="scss" scoped>
$min-height: 630px;

.ecommerce-submit-review-modal {
	display: flex;
	flex-direction: column;
	gap: 24px;
	min-height: $min-height;

	&__title {
		font-size: 40px;
		font-weight: 300;
		margin-top: 4px;
		text-align: left;
	}

	&__product {
		display: flex;
		align-items: center;
		gap: 16px;
	}

	&__product-image {
		border-radius: 4px;
	}

	&__product-title {
		font-size: 24px;
		font-weight: 300;
		text-align: left;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	&__form {
		display: flex;
		flex-direction: column;
		gap: 16px;
	}

	&__input {
		--input-fill-color: var(--color-light);
		/* stylelint-disable-next-line custom-property-pattern */
		--input-fill-color--hover: var(--color-light);
		--input-border-color: var(--color-gray);
		--input-border-width: 1px;
		--input-border-radius: 4px;

		align-items: flex-start;
		display: flex;
		flex-direction: column;
		gap: 8px;

		&:last-of-type {
			margin-bottom: 24px;
		}
	}

	&__button {
		--grid-button-primary-background-color: var(--color-dark);
		--grid-button-primary-background-color-hover: var(--color-dark);
		--grid-button-primary-border-radius: 4px;
		--grid-button-primary-color: var(--color-light);
		--grid-button-primary-color-hover: var(--color-light);
		--grid-button-primary-letter-spacing: 2px;
		--grid-button-primary-text-transform: uppercase;

		justify-content: center;
	}

	&__char-count {
		margin-top: -8px;
		color: var(--color-gray);
		font-size: 16px;
		text-align: right;
	}

	&__error {
		color: var(--color-danger-dark);
	}
}

.ecommerce-submit-review-modal-success {
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	min-height: $min-height;
	gap: 32px;
	padding: 32px;

	&__title {
		font-size: 40px;
		font-weight: 300;
	}

	&__description {
		font-size: 24px;
		font-weight: 300;
		text-align: center;
	}

	&__icon {
		color: var(--color-success);

		:deep(path) {
			animation: reveal 0.5s ease-in-out forwards;
		}
	}
}

@keyframes reveal {
	0% {
		clip-path: inset(0 100% 0 0);
	}

	100% {
		clip-path: inset(0 0 0 0);
	}
}

</style>
